import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  ClipboardPaste,
  Upload,
  Copy,
  LogOut,
  Check,
  AlertTriangle,
  Search as SearchIcon,
  X,
  Filter,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Trash2,
  Plus,
} from "lucide-react";

// Sentinel value used inside colFilters to represent "(empty)" selection
const EMPTY_FILTER = "__EMPTY__";

const FIELDS = [
  { key: "date", label: "Date", width: 110, readonly: false },
  { key: "categorie", label: "Catégorie", width: 130, readonly: false },
  { key: "qty", label: "Qty", width: 50, readonly: false, type: "int" },
  { key: "block", label: "Block", width: 60, readonly: false },
  { key: "rang", label: "Rang", width: 60, readonly: false },
  { key: "siege", label: "Siège", width: 60, readonly: false },
  { key: "prix", label: "Prix U.", width: 80, type: "money" },
  { key: "client", label: "Client", width: 90, sale: true },
  { key: "client_email", label: "Mail client", width: 160 },
  { key: "client_phone", label: "Phone", width: 120 },
  { key: "ref_vente", label: "Réf vente", width: 100, sale: true },
  { key: "contact_mail", label: "Contact mail", width: 160 },
  { key: "contact_whatsapp", label: "Contact WhatsApp", width: 150 },
  { key: "delivery_status", label: "Delivery", width: 110, type: "select", options: ["DELIVRED"] },
  { key: "supplier", label: "Supplier", width: 80 },
  { key: "total", label: "Total", width: 90, type: "money" },
  { key: "email", label: "Email", width: 180 },
  { key: "mdp", label: "Mdp", width: 130 },
  { key: "ref_commande", label: "Réf cmd", width: 110 },
  { key: "website", label: "Website", width: 110 },
];

function isSoldRow(t) {
  return Boolean((t.client || "").trim()) || Boolean((t.ref_vente || "").trim());
}

function fmtMoney(v) {
  if (v == null || v === "") return "";
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  if (Number.isNaN(n)) return "";
  return n.toFixed(2).replace(".", ",") + " €";
}

export default function Drive() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteRaw, setPasteRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [previewTab, setPreviewTab] = useState("sold_now");
  const [importing, setImporting] = useState(false);
  const [importInfo, setImportInfo] = useState(null);
  const [mapInfo, setMapInfo] = useState(null);
  const [colMapping, setColMapping] = useState({});
  const [mappedTsv, setMappedTsv] = useState("");
  const fileInputRef = useRef(null);

  // Filters
  const [status, setStatus] = useState("all"); // all | sold | unsold
  const [search, setSearch] = useState("");
  // Per-column filters: { fieldKey: Set<string> }
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null); // currently open column popup
  // Sort: { key: field key, dir: 'asc' | 'desc' } | null
  const [sort, setSort] = useState(null);
  // Row selection for bulk delete
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [blankDialog, setBlankDialog] = useState(null); // { date, count } | null
  const [blankBusy, setBlankBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tickets");
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Distinct values per column (used to populate filter dropdowns)
  const distinctByCol = useMemo(() => {
    const out = {};
    for (const f of FIELDS) {
      const s = new Set();
      let hasEmpty = false;
      for (const t of tickets) {
        let v = t[f.key];
        if (v == null || v === "") { hasEmpty = true; continue; }
        if (f.type === "money") v = fmtMoney(v);
        else v = String(v);
        s.add(v);
      }
      // Sort: dates by order_index, else alphabetical
      const arr = Array.from(s);
      if (f.key === "date") {
        const orderMap = new Map();
        for (const t of tickets) orderMap.set(t.date, t.order_index);
        arr.sort((a, b) => (orderMap.get(a) ?? 0) - (orderMap.get(b) ?? 0));
      } else {
        arr.sort((a, b) => a.localeCompare(b, "fr", { numeric: true }));
      }
      out[f.key] = { values: arr, hasEmpty };
    }
    return out;
  }, [tickets]);

  // Helpers to read/write column filters
  const colHas = (key, value) => colFilters[key]?.has(value) ?? false;
  const colToggle = (key, value) => {
    setColFilters((prev) => {
      const next = { ...prev };
      const s = new Set(next[key] || []);
      if (s.has(value)) s.delete(value);
      else s.add(value);
      if (s.size === 0) delete next[key];
      else next[key] = s;
      return next;
    });
  };
  const colClear = (key) => {
    setColFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Apply filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = tickets.filter((t) => {
      const sold = isSoldRow(t);
      const delivered = String(t.delivery_status || "").trim() !== "";
      if (status === "sold" && !sold) return false;
      if (status === "unsold" && sold) return false;
      if (status === "delivered" && !delivered) return false;

      // Per-column filters
      for (const [key, sel] of Object.entries(colFilters)) {
        if (!sel || sel.size === 0) continue;
        const f = FIELDS.find((x) => x.key === key);
        let v = t[key];
        const isEmpty = v == null || v === "";
        if (isEmpty) {
          // Only match if "(vide)" sentinel is selected
          if (!sel.has(EMPTY_FILTER)) return false;
          continue;
        }
        if (f?.type === "money") v = fmtMoney(v);
        else v = String(v);
        if (!sel.has(v)) return false;
      }

      if (q) {
        const hay = [
          t.date, t.categorie, t.block, t.rang, t.siege,
          t.client, t.ref_vente, t.supplier, t.email, t.mdp,
          t.ref_commande, t.website,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // Default multi-level sort within each date group: Catégorie → Block → Rang → Siège
    const naturalCompare = (a, b) => {
      const aEmpty = a == null || a === "";
      const bEmpty = b == null || b === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return String(a).localeCompare(String(b), "fr", { numeric: true, sensitivity: "base" });
    };

    if (sort?.key) {
      // User-chosen sort takes priority, with natural tiebreaker
      const f = FIELDS.find((x) => x.key === sort.key);
      const numeric = f?.type === "money" || f?.type === "int" || ["rang", "siege", "block"].includes(sort.key);
      const dir = sort.dir === "desc" ? -1 : 1;
      arr.sort((a, b) => {
        const va = a[sort.key];
        const vb = b[sort.key];
        const aEmpty = va == null || va === "";
        const bEmpty = vb == null || vb === "";
        if (aEmpty && bEmpty) {
          // tiebreak with natural order
        } else if (aEmpty) {
          return 1;
        } else if (bEmpty) {
          return -1;
        } else {
          if (numeric) {
            const na = parseFloat(String(va).replace(",", "."));
            const nb = parseFloat(String(vb).replace(",", "."));
            if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return (na - nb) * dir;
          }
          const cmp = String(va).localeCompare(String(vb), "fr", { numeric: true }) * dir;
          if (cmp !== 0) return cmp;
        }
        // Natural tiebreaker: Catégorie → Block → Rang → Siège
        return (
          naturalCompare(a.categorie, b.categorie) ||
          naturalCompare(a.block, b.block) ||
          naturalCompare(a.rang, b.rang) ||
          naturalCompare(a.siege, b.siege)
        );
      });
    } else {
      // No explicit sort → apply default multi-level natural sort
      arr.sort(
        (a, b) =>
          naturalCompare(a.categorie, b.categorie) ||
          naturalCompare(a.block, b.block) ||
          naturalCompare(a.rang, b.rang) ||
          naturalCompare(a.siege, b.siege),
      );
    }
    return arr;
  }, [tickets, status, colFilters, search, sort]);

  // Group filtered rows by date for visual separators
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      if (!map.has(t.date)) {
        map.set(t.date, { date: t.date, order_index: t.order_index, items: [] });
      }
      map.get(t.date).items.push(t);
    }
    return Array.from(map.values()).sort(
      (a, b) => a.order_index - b.order_index,
    );
  }, [filtered]);

  // Rows needing external pause acknowledgement
  const pendingAlerts = useMemo(
    () => tickets.filter((t) => isSoldRow(t) && !t.sale_alert_acknowledged),
    [tickets],
  );

  const activeFilterCount =
    (status !== "all" ? 1 : 0) +
    Object.values(colFilters).reduce((acc, s) => acc + (s?.size || 0), 0) +
    (search.trim() ? 1 : 0) +
    (sort ? 1 : 0);

  const resetFilters = () => {
    setStatus("all");
    setColFilters({});
    setSearch("");
    setSort(null);
  };

  // === Rectangular cell selection (Excel-like) ===
  const [selection, setSelection] = useState(null); // {startRow, startCol, endRow, endCol}
  const [dragAnchor, setDragAnchor] = useState(null); // {row, col} while mouse is down
  const isDraggingRef = useRef(false);

  // Build a flat visual order matching what's rendered (respects grouping + sort)
  const visualOrder = useMemo(() => {
    const out = [];
    for (const g of grouped) for (const t of g.items) out.push(t);
    return out;
  }, [grouped]);

  const normSel = (s) => {
    if (!s) return null;
    return {
      r1: Math.min(s.startRow, s.endRow),
      r2: Math.max(s.startRow, s.endRow),
      c1: Math.min(s.startCol, s.endCol),
      c2: Math.max(s.startCol, s.endCol),
    };
  };
  const normalized = useMemo(() => normSel(selection), [selection]);

  const isCellSelected = (row, col) => {
    if (!normalized) return false;
    return (
      row >= normalized.r1 &&
      row <= normalized.r2 &&
      col >= normalized.c1 &&
      col <= normalized.c2
    );
  };

  const onCellMouseDown = (row, col, e) => {
    // Ignore right click / modifier-only combos
    if (e.button !== 0) return;
    // Shift+click → extend existing selection (or create from current anchor)
    if (e.shiftKey && selection) {
      setSelection({ startRow: selection.startRow, startCol: selection.startCol, endRow: row, endCol: col });
      e.preventDefault();
      return;
    }
    setDragAnchor({ row, col });
    isDraggingRef.current = false;
    // Do NOT clear selection yet — we'll do it on mouseup if it turns out to be a single click
  };

  const onCellMouseEnter = (row, col) => {
    if (!dragAnchor) return;
    if (dragAnchor.row === row && dragAnchor.col === col) return;
    // Started drag → mark selecting and update
    isDraggingRef.current = true;
    setSelection({
      startRow: dragAnchor.row,
      startCol: dragAnchor.col,
      endRow: row,
      endCol: col,
    });
  };

  const onCellMouseUp = (row, col, startEditFn) => {
    if (!dragAnchor) return;
    if (!isDraggingRef.current) {
      // Simple click — clear selection & delegate to editor
      setSelection(null);
      startEditFn?.();
    }
    setDragAnchor(null);
    isDraggingRef.current = false;
  };

  const convertForField = (field, raw) => {
    if (field.type === "int") {
      const n = parseInt(String(raw).replace(/[^\d-]/g, ""), 10);
      return Number.isNaN(n) || n <= 0 ? undefined : n;
    }
    if (field.type === "money") {
      const cleaned = String(raw).replace(/[€\s]/g, "").replace(",", ".");
      if (cleaned === "") return null;
      const n = parseFloat(cleaned);
      return Number.isNaN(n) ? undefined : n;
    }
    return String(raw ?? "").trim();
  };

  const applyPasteToSelection = async (text) => {
    const rowsData = text.replace(/\r/g, "").split("\n");
    while (rowsData.length && rowsData[rowsData.length - 1] === "") rowsData.pop();
    if (!rowsData.length) return;
    const block = rowsData.map((l) => l.split("\t"));
    const single = block.length === 1 && block[0].length === 1;
    const updates = new Map();
    let cells = 0;
    const addUpdate = (t, field, raw) => {
      if (!t || !field) return;
      const v = convertForField(field, raw);
      if (v === undefined) return;
      if (!updates.has(t.id)) updates.set(t.id, {});
      updates.get(t.id)[field.key] = v;
      cells += 1;
    };
    if (single) {
      for (let r = normalized.r1; r <= normalized.r2; r += 1) {
        const t = visualOrder[r];
        for (let c = normalized.c1; c <= normalized.c2; c += 1) {
          addUpdate(t, FIELDS[c], block[0][0]);
        }
      }
    } else {
      for (let i = 0; i < block.length; i += 1) {
        const t = visualOrder[normalized.r1 + i];
        for (let j = 0; j < block[i].length; j += 1) {
          addUpdate(t, FIELDS[normalized.c1 + j], block[i][j]);
        }
      }
    }
    if (!cells) return;
    const entries = [...updates.entries()];
    const results = await Promise.allSettled(
      entries.map(([id, fields]) => api.patch(`/admin/tickets/${id}`, fields))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed) toast.error(`${failed} ligne(s) n'ont pas pu être collées (valeur invalide ?)`);
    toast.success(`${cells} cellule(s) collée(s) sur ${entries.length - failed} ligne(s)`);
    setSelection(null);
    load();
  };

  // Global Ctrl/Cmd+C to copy selection as TSV, and Escape to clear
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelection(null);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
        if (!normalized) return;
        // Skip when user is typing in an input/textarea
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        const lines = [];
        for (let r = normalized.r1; r <= normalized.r2; r += 1) {
          const t = visualOrder[r];
          if (!t) continue;
          const cells = [];
          for (let c = normalized.c1; c <= normalized.c2; c += 1) {
            const f = FIELDS[c];
            let v = t[f.key];
            if (v == null || v === "") v = "";
            else if (f.type === "money") v = fmtMoney(v);
            else v = String(v);
            cells.push(v);
          }
          lines.push(cells.join("\t"));
        }
        const text = lines.join("\n");
        const nCells = (normalized.r2 - normalized.r1 + 1) * (normalized.c2 - normalized.c1 + 1);
        (async () => {
          try {
            await navigator.clipboard.writeText(text);
            toast.success(`${nCells} cellule(s) copiée(s) dans le presse-papier`);
          } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            try {
              document.execCommand("copy");
              toast.success(`${nCells} cellule(s) copiée(s)`);
            } catch {
              toast.error("Copie impossible");
            }
            document.body.removeChild(ta);
          }
        })();
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const onPaste = (e) => {
      if (!normalized) return;
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const text = e.clipboardData?.getData("text/plain") ?? "";
      if (!text.trim()) return;
      e.preventDefault();
      applyPasteToSelection(text);
    };
    document.addEventListener("paste", onPaste);
    // Release dragAnchor if mouseup happens outside a cell
    const onDocMouseUp = () => {
      if (dragAnchor && !isDraggingRef.current) {
        setDragAnchor(null);
      } else if (dragAnchor && isDraggingRef.current) {
        setDragAnchor(null);
        isDraggingRef.current = false;
      }
    };
    document.addEventListener("mouseup", onDocMouseUp);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mouseup", onDocMouseUp);
      document.removeEventListener("paste", onPaste);
    };
  }, [normalized, visualOrder, dragAnchor]);
  const copyColumn = async (field) => {
    const vals = filtered.map((t) => {
      const v = t[field.key];
      if (v == null || v === "") return "";
      if (field.type === "money") return fmtMoney(v);
      return String(v);
    });
    const text = vals.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${vals.length} valeur(s) copiée(s) — colonne ${field.label}`);
    } catch {
      // Fallback for environments without clipboard permission
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success(`${vals.length} valeur(s) copiée(s) — colonne ${field.label}`);
      } catch {
        toast.error("Impossible de copier — copiez manuellement");
      }
      document.body.removeChild(ta);
    }
  };
  const onCellSave = async (id, key, rawValue) => {
    const field = FIELDS.find((f) => f.key === key);
    let value = rawValue;
    if (field?.type === "int") {
      const n = parseInt(String(rawValue).replace(/[^\d-]/g, ""), 10);
      value = Number.isNaN(n) ? null : n;
      if (value !== null && value <= 0) {
        toast.error("Quantité invalide");
        return;
      }
    } else if (field?.type === "money") {
      const cleaned = String(rawValue).replace(/[€\s]/g, "").replace(",", ".");
      if (cleaned === "") {
        value = null;
      } else {
        const n = parseFloat(cleaned);
        if (Number.isNaN(n)) {
          toast.error("Montant invalide");
          return;
        }
        value = n;
      }
    } else {
      value = String(rawValue || "").trim();
    }
    try {
      const { data } = await api.patch(`/admin/tickets/${id}`, { [key]: value });
      setTickets((prev) => prev.map((t) => (t.id === id ? data : t)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      load();
    }
  };

  const submitBlankRows = async () => {
    if (!blankDialog?.date?.trim()) {
      toast.error("Indiquez une date");
      return;
    }
    setBlankBusy(true);
    try {
      const { data } = await api.post("/admin/tickets/blank", {
        date: blankDialog.date.trim(),
        count: Math.max(1, parseInt(blankDialog.count, 10) || 1),
      });
      toast.success(`${data.inserted} ligne(s) vide(s) ajoutée(s) pour ${data.date}`);
      setBlankDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBlankBusy(false);
    }
  };

  const onAnalyze = async () => {
    if (!pasteRaw.trim()) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/admin/drive/import/analyze", { raw: pasteRaw });
      setMapInfo(data);
      const m = {};
      data.columns.forEach((c) => {
        m[c.index] = c.suggested || "";
      });
      setColMapping(m);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onMappingChange = (index, field) => {
    setColMapping((prev) => {
      const next = { ...prev };
      if (field) {
        Object.keys(next).forEach((k) => {
          if (String(k) !== String(index) && next[k] === field) next[k] = "";
        });
      }
      next[index] = field;
      return next;
    });
  };

  const hasRequiredMapping =
    Object.values(colMapping).includes("date") && Object.values(colMapping).includes("categorie");

  const onMappedPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/admin/drive/import/map", {
        raw: pasteRaw,
        mapping: colMapping,
        has_header: mapInfo?.has_header || false,
      });
      setMappedTsv(data.tsv);
      const res = await api.post("/admin/drive/sync/preview", { raw: data.tsv });
      setPreview(res.data);
      const order = ["sold_now", "new_rows", "updated", "missing", "unchanged"];
      setPreviewTab(order.find((k) => (res.data[k] || []).length > 0) || "sold_now");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onApplySync = async () => {
    const raw = mappedTsv || pasteRaw;
    if (!raw.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/drive/sync/apply", {
        raw,
        delete_missing: false,
      });
      toast.success(
        `${data.inserted} ajout(s) · ${data.sold_now} mise(s) en pause (ventes) · ${data.updated} maj · ${data.deleted} suppr.`,
      );
      setPasteRaw("");
      setPreview(null);
      setDeleteMissing(false);
      setPasteOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const closePasteDialog = () => {
    setPasteOpen(false);
    setPreview(null);
    setDeleteMissing(false);
    setImportInfo(null);
    setMapInfo(null);
    setColMapping({});
    setMappedTsv("");
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/drive/import/file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPasteRaw(data.tsv);
      setImportInfo({
        name: f.name,
        rows: data.rows_parsed,
        duplicates: data.duplicates_removed,
        mapping: data.mapping || {},
        ignored: data.ignored_columns || [],
      });
      toast.success(`${data.rows_parsed} ligne(s) détectée(s) dans « ${f.name} »`);
      setPreviewLoading(true);
      const res = await api.post("/admin/drive/sync/preview", { raw: data.tsv });
      setPreview(res.data);
      const order = ["sold_now", "new_rows", "updated", "missing", "unchanged"];
      setPreviewTab(order.find((k) => (res.data[k] || []).length > 0) || "sold_now");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
      setPreviewLoading(false);
    }
  };

  const ackAll = async () => {
    if (pendingAlerts.length === 0) return;
    try {
      await api.post("/admin/drive/ack", {
        ids: pendingAlerts.map((t) => t.id),
      });
      setTickets((prev) =>
        prev.map((t) =>
          pendingAlerts.find((p) => p.id === t.id)
            ? { ...t, sale_alert_acknowledged: true }
            : t,
        ),
      );
      toast.success("C'est noté.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((t) => selectedRows.has(t.id));

  const toggleSelectAll = () => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((t) => next.delete(t.id));
      } else {
        filtered.forEach((t) => next.add(t.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDeleteSelected = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/tickets/bulk", {
        action: "delete",
        ids: [...selectedRows],
      });
      toast.success(`${data.affected} ligne(s) supprimée(s)`);
      setSelectedRows(new Set());
      setConfirmDelete(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="drive-page">
      {/* Top bar */}
      <header className="border-b border-neutral-200 sticky top-0 bg-white z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-6 min-w-0">
            <button
              onClick={() => nav("/admin")}
              className="flex items-center gap-2 border-2 border-black px-3 py-2 text-[11px] sm:text-xs uppercase tracking-widest font-semibold hover:bg-black hover:text-white transition-colors"
              data-testid="drive-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Stock</span>
            </button>
            <div className="min-w-0">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Console admin · Drive
              </div>
              <div className="font-serif-display text-xl sm:text-2xl truncate">
                Céline Dion · Ventes
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            <Button
              onClick={() => setBlankDialog({ date: "", count: 1 })}
              variant="outline"
              className="rounded-none border-black hover:bg-neutral-50"
              data-testid="drive-add-blank-open"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Lignes vides</span>
            </Button>
            <Button
              onClick={() => setPasteOpen(true)}
              className="rounded-none bg-black text-white hover:bg-neutral-800"
              data-testid="drive-paste-open"
            >
              <ClipboardPaste className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Synchroniser Drive</span>
            </Button>
            <span
              className="hidden md:inline-block font-mono-data text-[11px] text-neutral-500 max-w-[160px] truncate"
              data-testid="drive-user-email"
            >
              {user?.email}
            </span>
            <Button
              onClick={onLogout}
              variant="outline"
              className="rounded-none border-black hover:bg-neutral-50 px-3"
              data-testid="drive-logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Alert banner */}
      {pendingAlerts.length > 0 && (
        <div
          className="sticky top-[64px] sm:top-[72px] z-10 bg-amber-50 border-b border-amber-300 px-4 sm:px-6 py-3"
          data-testid="drive-pause-alert"
        >
          <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-semibold text-amber-900 text-sm">
                  Pense à mettre en pause sur les sites de vente
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {pendingAlerts.length} ligne(s) vendue(s) à mettre en pause manuellement sur Ticketmaster / FNAC / AXS.
                </div>
              </div>
            </div>
            <Button
              onClick={ackAll}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
              data-testid="drive-pause-ack"
            >
              <Check className="w-4 h-4 mr-2" />
              C'est fait
            </Button>
          </div>
        </div>
      )}

      {/* Filters bar */}
      <section className="border-b border-neutral-200 bg-white" data-testid="drive-filters">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 border border-black">
            {[
              { v: "all", label: "Tous" },
              { v: "unsold", label: "En vente" },
              { v: "sold", label: "Vendus" },
              { v: "delivered", label: "Delivred" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setStatus(opt.v)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                  status === opt.v
                    ? opt.v === "delivered"
                      ? "bg-red-600 text-white"
                      : "bg-black text-white"
                    : "bg-white text-black hover:bg-neutral-100"
                }`}
                data-testid={`drive-filter-status-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher : email, client, réf, supplier…"
              className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black pl-9"
              data-testid="drive-filter-search"
            />
          </div>

          <div className="ml-auto flex items-center gap-3 text-[11px] font-mono-data uppercase tracking-widest text-neutral-500">
            <span data-testid="drive-results-count">
              {filtered.length} / {tickets.length} ligne(s)
            </span>
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1 text-red-600 hover:text-red-700"
                data-testid="drive-filter-reset"
              >
                <X className="w-3.5 h-3.5" />
                Réinitialiser ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main table */}
      <main className="max-w-[1600px] mx-auto px-2 sm:px-4 py-6">
        <div className="border border-neutral-200" data-testid="drive-table-wrapper">
          <table className="w-full text-xs font-mono-data" style={{ minWidth: "1400px" }}>
            <thead
              className={`sticky z-[5] ${
                pendingAlerts.length > 0 ? "top-[136px] sm:top-[152px]" : "top-[60px] sm:top-[72px]"
              }`}
            >
              <tr className="bg-neutral-900">
                <th className="bg-neutral-900 px-2 py-2.5 border-b border-neutral-700 w-[36px] shadow-[0_2px_0_rgba(0,0,0,0.15)]">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-amber-400 cursor-pointer align-middle"
                    title={`Sélectionner les ${filtered.length} ligne(s) visibles`}
                    data-testid="drive-select-all"
                  />
                </th>
                {FIELDS.map((f) => {
                  const sel = colFilters[f.key];
                  const count = sel?.size || 0;
                  return (
                    <th
                      key={f.key}
                      style={{ minWidth: f.width }}
                      className="bg-neutral-900 text-left uppercase tracking-widest text-[10px] text-white px-2 py-2.5 border-b border-neutral-700 whitespace-nowrap font-semibold relative shadow-[0_2px_0_rgba(0,0,0,0.15)]"
                    >
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSort((prev) => {
                              if (!prev || prev.key !== f.key) return { key: f.key, dir: "asc" };
                              if (prev.dir === "asc") return { key: f.key, dir: "desc" };
                              return null; // 3rd click clears
                            });
                          }}
                          className="flex items-center gap-1 hover:text-amber-300 transition-colors"
                          data-testid={`drive-col-sort-${f.key}`}
                          aria-label={`Trier par ${f.label}`}
                        >
                          <span>{f.label}</span>
                          {sort?.key === f.key ? (
                            sort.dir === "asc" ? (
                              <ArrowUp className="w-3 h-3 text-amber-300" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-amber-300" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-neutral-600" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilter(openFilter === f.key ? null : f.key);
                          }}
                          className={`p-0.5 rounded transition-colors ${
                            count > 0
                              ? "text-amber-300"
                              : "text-neutral-500 hover:text-white"
                          }`}
                          data-testid={`drive-col-filter-${f.key}`}
                          aria-label={`Filtrer ${f.label}`}
                        >
                          <Filter className="w-3 h-3" fill={count > 0 ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyColumn(f);
                          }}
                          className="p-0.5 rounded transition-colors text-neutral-500 hover:text-emerald-300"
                          data-testid={`drive-col-copy-${f.key}`}
                          aria-label={`Copier la colonne ${f.label}`}
                          title={`Copier ${filtered.length} valeur(s) de la colonne ${f.label}`}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        {count > 0 && (
                          <span className="text-[9px] bg-amber-300 text-black px-1 leading-tight font-bold">
                            {count}
                          </span>
                        )}
                      </div>
                      {openFilter === f.key && (
                        <ColumnFilterPopup
                          values={distinctByCol[f.key]?.values || []}
                          hasEmpty={distinctByCol[f.key]?.hasEmpty || false}
                          selected={sel || new Set()}
                          onToggle={(v) => colToggle(f.key, v)}
                          onClear={() => {
                            colClear(f.key);
                            setOpenFilter(null);
                          }}
                          onClose={() => setOpenFilter(null)}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody onCopy={(e) => {
              // Prevent default browser row-wise copy when a rectangular selection is active
              if (normalized) e.preventDefault();
            }}>
              {(() => {
                let visualRow = -1;
                return grouped.map((g) => (
                  <Fragment key={g.date}>
                    <tr className="bg-neutral-200">
                      <td
                        colSpan={FIELDS.length + 1}
                        className="font-serif-display text-base text-neutral-900 px-3 py-2 capitalize border-t-2 border-b-2 border-neutral-400"
                      >
                        {g.date}
                        <span className="ml-3 font-mono-data text-[10px] uppercase tracking-widest text-neutral-700">
                          {g.items.length} ligne(s) ·{" "}
                          <span className="text-emerald-700 font-semibold">
                            {g.items.filter(isSoldRow).length} vendue(s)
                          </span>
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setBlankDialog({ date: g.date, count: 1 });
                          }}
                          className="ml-3 inline-flex items-center gap-1 border border-neutral-500 bg-white px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-widest text-neutral-700 hover:bg-black hover:text-white transition-colors align-middle"
                          title={`Ajouter des lignes vides pour ${g.date}`}
                          data-testid={`drive-add-blank-${g.date.replace(/\s+/g, "-")}`}
                        >
                          <Plus className="w-3 h-3" /> lignes
                        </button>
                      </td>
                    </tr>
                    {g.items.map((t) => {
                      visualRow += 1;
                      const rowIdx = visualRow;
                      const sold = isSoldRow(t);
                      const delivered = String(t.delivery_status || "").trim() !== "";
                      return (
                        <tr
                          key={t.id}
                          className={`${
                            delivered
                              ? "bg-red-100/70 text-red-950 border-l-4 border-l-red-400"
                              : sold
                                ? "bg-emerald-100/80 text-emerald-950 border-l-4 border-l-emerald-500"
                                : "bg-white text-neutral-900"
                          } hover:bg-amber-50 transition-colors`}
                          data-testid={`drive-row-${t.id}`}
                          data-sold={sold}
                          data-delivered={delivered}
                        >
                          <td className="px-2 border-b border-neutral-100 w-[36px]">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(t.id)}
                              onChange={() => toggleRow(t.id)}
                              onMouseDown={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-black cursor-pointer align-middle"
                              data-testid={`drive-row-check-${t.id}`}
                            />
                          </td>
                          {FIELDS.map((f, colIdx) => (
                            <Cell
                              key={f.key}
                              field={f}
                              value={t[f.key]}
                              sold={sold}
                              onSave={(v) => onCellSave(t.id, f.key, v)}
                              rowIdx={rowIdx}
                              colIdx={colIdx}
                              isSelected={isCellSelected(rowIdx, colIdx)}
                              onCellMouseDown={onCellMouseDown}
                              onCellMouseEnter={onCellMouseEnter}
                              onCellMouseUp={onCellMouseUp}
                            />
                          ))}
                        </tr>
                      );
                    })}
                  </Fragment>
                ));
              })()}
              {tickets.length === 0 && (
                <tr>
                  <td
                    colSpan={FIELDS.length + 1}
                    className="text-center text-sm text-neutral-500 py-12"
                  >
                    Aucune ligne. Cliquez sur "Synchroniser Drive" pour importer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Paste & Sync dialog */}
      <AlertDialog open={pasteOpen} onOpenChange={(o) => (o ? setPasteOpen(true) : closePasteDialog())}>
        <AlertDialogTrigger asChild>
          <span className="hidden" />
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">
              {preview
                ? "Aperçu de la synchronisation"
                : mapInfo
                  ? "Associer les colonnes"
                  : "Synchroniser avec Google Sheets"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {!preview && !mapInfo && (
                <>
                  Sélectionnez toutes vos lignes dans Google Sheets (Ctrl+A puis Ctrl+C), puis collez ici.
                  Vous choisirez ensuite <strong>quelle colonne va où</strong>, avec un <strong>aperçu</strong> avant
                  d'appliquer.
                </>
              )}
              {!preview && mapInfo && (
                <>
                  Vérifiez où va chaque colonne (pré-rempli automatiquement) et corrigez si besoin.
                  <strong> Date</strong> et <strong>Catégorie</strong> sont obligatoires.
                </>
              )}
              {preview && (
                <>
                  Comparaison terminée. Vérifiez ce qui va changer puis cliquez sur <strong>Appliquer</strong>.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {importInfo && (
            <div
              className="border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1"
              data-testid="drive-import-info"
            >
              <div>
                <strong>{importInfo.name}</strong> — {importInfo.rows} ligne(s) détectée(s)
                {importInfo.duplicates > 0 && <> · {importInfo.duplicates} doublon(s) interne(s) retiré(s)</>}
              </div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(importInfo.mapping).map(([field, col]) => (
                  <span key={field} className="border border-emerald-300 bg-white px-1.5 py-0.5 font-mono-data text-[10px]">
                    {field} ← {col}
                  </span>
                ))}
              </div>
              {importInfo.ignored.length > 0 && (
                <div className="text-emerald-700">
                  Colonnes ignorées : {importInfo.ignored.join(", ")}
                </div>
              )}
            </div>
          )}

          {!preview && !mapInfo && (
            <>
              <div className="border-2 border-dashed border-neutral-300 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-neutral-600">
                  <strong>Importer un fichier</strong> (.numbers, Excel .xlsx, .csv, .tsv) — les colonnes sont
                  détectées automatiquement, même dans le désordre.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".numbers,.xlsx,.xlsm,.csv,.tsv,.txt"
                  onChange={onImportFile}
                  className="hidden"
                  data-testid="drive-import-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                  className="rounded-none border-black hover:bg-neutral-50 shrink-0"
                  data-testid="drive-import-file-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? "Analyse du fichier…" : "Choisir un fichier"}
                </Button>
              </div>
              <div className="text-center font-mono-data text-[10px] uppercase tracking-widest text-neutral-400">
                — ou collez depuis Google Sheets —
              </div>
              <Textarea
                value={pasteRaw}
                onChange={(e) => setPasteRaw(e.target.value)}
                placeholder="Collez ici toutes les lignes depuis Google Sheets (Ctrl+V)…"
                rows={10}
                className="rounded-none font-mono-data text-xs border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="drive-paste-textarea"
              />
              <div className="font-mono-data text-[10px] text-neutral-500 -mt-1">
                {pasteRaw.split("\n").filter((l) => l.trim()).length} ligne(s) collée(s)
              </div>
            </>
          )}

          {!preview && mapInfo && (
            <div className="flex-1 overflow-hidden flex flex-col gap-2">
              <div className="text-xs text-neutral-600 border border-neutral-200 bg-neutral-50 px-3 py-2">
                <strong>{mapInfo.data_rows} ligne(s)</strong> · {mapInfo.columns.length} colonne(s)
                {mapInfo.has_header ? " · ligne d'en-têtes détectée (elle sera ignorée)" : ""}
              </div>
              <div className="border border-neutral-200 overflow-auto flex-1 min-h-[220px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-neutral-900 text-white">
                    <tr>
                      <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">Colonne collée</th>
                      <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">Exemples</th>
                      <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">→ Va dans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mapInfo.columns.map((c) => (
                      <tr key={c.index} className="border-b border-neutral-100 hover:bg-amber-50">
                        <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                          {c.header || `Colonne ${c.index + 1}`}
                        </td>
                        <td className="px-3 py-2 font-mono-data text-neutral-500 max-w-[300px] truncate">
                          {c.samples.join(" · ") || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={colMapping[c.index] || ""}
                            onChange={(e) => onMappingChange(c.index, e.target.value)}
                            className={`border px-2 py-1.5 bg-white w-full max-w-[190px] text-xs ${
                              colMapping[c.index]
                                ? "border-black font-semibold"
                                : "border-neutral-300 text-neutral-400"
                            }`}
                            data-testid={`map-col-select-${c.index}`}
                          >
                            <option value="">— Ignorer —</option>
                            {FIELDS.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!hasRequiredMapping && (
                <div className="text-xs text-red-600" data-testid="map-required-warning">
                  Affectez au minimum une colonne à <strong>Date</strong> et une à <strong>Catégorie</strong>.
                </div>
              )}
            </div>
          )}

          {preview && (
            <div className="flex-1 overflow-hidden flex flex-col gap-3">
              {/* Summary tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 border border-neutral-200">
                {[
                  { key: "sold_now", label: "Nouvelles ventes", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                  { key: "new_rows", label: "Nouvelles lignes", color: "bg-blue-50 text-blue-700 border-blue-200" },
                  { key: "updated", label: "Mises à jour", color: "bg-amber-50 text-amber-700 border-amber-200" },
                  { key: "missing", label: "Manquantes (DB)", color: "bg-red-50 text-red-700 border-red-200" },
                  { key: "unchanged", label: "Inchangées", color: "bg-neutral-50 text-neutral-600 border-neutral-200" },
                ].map((tile) => {
                  const n = (preview[tile.key] || []).length;
                  const active = previewTab === tile.key;
                  return (
                    <button
                      key={tile.key}
                      onClick={() => setPreviewTab(tile.key)}
                      className={`text-left p-3 border-r last:border-r-0 transition-colors ${
                        active ? "bg-black text-white border-black" : tile.color + " hover:opacity-80"
                      }`}
                      data-testid={`sync-preview-tab-${tile.key}`}
                    >
                      <div className={`text-[10px] uppercase tracking-widest ${active ? "text-neutral-300" : "opacity-70"}`}>
                        {tile.label}
                      </div>
                      <div className="font-serif-display text-2xl">{n}</div>
                    </button>
                  );
                })}
              </div>

              {/* List for selected tab */}
              <div className="border border-neutral-200 overflow-auto flex-1 min-h-[260px]">
                <SyncPreviewList tab={previewTab} rows={preview[previewTab] || []} />
              </div>

              {/* Info: missing lines are NEVER deleted automatically (security) */}
              {(preview.missing || []).length > 0 && (
                <div className="flex items-start gap-2 text-xs border border-neutral-300 bg-neutral-50 p-3 text-neutral-700">
                  <span>
                    <strong>{preview.missing.length} ligne(s)</strong> sont en stock mais ne figurent pas
                    dans le Drive collé. Elles seront <strong>conservées telles quelles</strong> pour
                    éviter toute perte accidentelle. Pour les retirer, désactivez-les ou supprimez-les
                    manuellement depuis l'onglet <strong>Stock</strong>.
                  </span>
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-none"
              data-testid="drive-paste-cancel"
              onClick={closePasteDialog}
            >
              Annuler
            </AlertDialogCancel>
            {!preview && !mapInfo && (
              <Button
                onClick={onAnalyze}
                disabled={previewLoading || !pasteRaw.trim()}
                className="rounded-none bg-black hover:bg-neutral-800 text-white"
                data-testid="drive-map-columns"
              >
                {previewLoading ? "Analyse…" : "Vérifier les colonnes →"}
              </Button>
            )}
            {!preview && mapInfo && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setMapInfo(null)}
                  className="rounded-none border-black"
                  data-testid="drive-map-back"
                >
                  ← Retour
                </Button>
                <Button
                  onClick={onMappedPreview}
                  disabled={previewLoading || !hasRequiredMapping}
                  className="rounded-none bg-black hover:bg-neutral-800 text-white"
                  data-testid="drive-sync-preview"
                >
                  {previewLoading ? "Analyse…" : "Calculer l'aperçu →"}
                </Button>
              </>
            )}
            {preview && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setPreview(null)}
                  className="rounded-none border-black"
                  data-testid="drive-sync-back"
                >
                  ← Retour
                </Button>
                <Button
                  onClick={onApplySync}
                  disabled={busy}
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="drive-sync-apply"
                >
                  {busy ? "Application…" : "✓ Appliquer la synchronisation"}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Selection action bar */}
      {selectedRows.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-black text-white shadow-2xl px-4 py-3 flex items-center gap-4"
          data-testid="drive-selection-bar"
        >
          <span className="font-mono-data text-xs uppercase tracking-widest">
            {selectedRows.size} ligne(s) sélectionnée(s)
          </span>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="text-xs underline text-neutral-300 hover:text-white"
            data-testid="drive-selection-clear"
          >
            Désélectionner
          </button>
          <Button
            onClick={() => setConfirmDelete(true)}
            className="rounded-none bg-red-600 hover:bg-red-700 text-white h-9"
            data-testid="drive-delete-selected"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer ({selectedRows.size})
          </Button>
        </div>
      )}

      {/* Blank rows dialog */}
      <AlertDialog open={!!blankDialog} onOpenChange={(o) => !o && setBlankDialog(null)}>
        <AlertDialogContent className="rounded-none max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">
              Ajouter des lignes vides
            </AlertDialogTitle>
            <AlertDialogDescription>
              Les lignes seront créées vides pour la date choisie — remplissez-les ensuite
              directement dans le tableau. Elles n'apparaissent pas au public tant que la
              catégorie n'est pas remplie.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-neutral-500">
                Date
              </label>
              <Input
                value={blankDialog?.date || ""}
                onChange={(e) => setBlankDialog((p) => ({ ...p, date: e.target.value }))}
                placeholder="ex : sam 12 sept ou 12/09/2026"
                className="rounded-none border-neutral-400 mt-1"
                data-testid="blank-rows-date"
              />
            </div>
            <div>
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-neutral-500">
                Nombre de lignes
              </label>
              <Input
                type="number"
                min={1}
                max={100}
                value={blankDialog?.count ?? 1}
                onChange={(e) => setBlankDialog((p) => ({ ...p, count: e.target.value }))}
                className="rounded-none border-neutral-400 mt-1 w-28"
                data-testid="blank-rows-count"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="blank-rows-cancel">
              Annuler
            </AlertDialogCancel>
            <Button
              onClick={submitBlankRows}
              disabled={blankBusy}
              className="rounded-none bg-black hover:bg-neutral-800 text-white"
              data-testid="blank-rows-submit"
            >
              {blankBusy ? "Ajout…" : "Ajouter"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">
              Supprimer {selectedRows.size} ligne(s) ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Les lignes sélectionnées seront retirées du stock
              et du marketplace public.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="drive-delete-cancel">
              Annuler
            </AlertDialogCancel>
            <Button
              onClick={onDeleteSelected}
              disabled={deleting}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              data-testid="drive-delete-confirm"
            >
              {deleting ? "Suppression…" : "Oui, supprimer"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** end of Drive component */

function ColumnFilterPopup({ values, hasEmpty, selected, onToggle, onClear, onClose }) {
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return values;
    return values.filter((v) => v.toLowerCase().includes(term));
  }, [q, values]);

  const emptyOn = selected.has(EMPTY_FILTER);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-1 w-64 bg-neutral-900 border border-neutral-700 shadow-2xl z-[5] text-white"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-2">
        <div className="relative">
          <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="w-full bg-neutral-800 border border-neutral-700 pl-7 pr-2 py-1.5 text-xs text-white placeholder-neutral-500 focus:outline-none focus:border-neutral-500 normal-case tracking-normal"
            data-testid="drive-col-popup-search"
          />
        </div>
      </div>
      <button
        onClick={onClear}
        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left border-b border-neutral-700 hover:bg-neutral-800 transition-colors ${
          selected.size === 0 ? "text-neutral-500" : "text-white"
        }`}
        disabled={selected.size === 0}
        data-testid="drive-col-popup-clear"
      >
        <X className="w-3.5 h-3.5" />
        Effacer les filtres
      </button>
      <div className="max-h-64 overflow-y-auto py-1">
        {hasEmpty && (
          <button
            onClick={() => onToggle(EMPTY_FILTER)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-neutral-800 transition-colors normal-case tracking-normal border-b border-neutral-800 ${
              emptyOn ? "bg-neutral-800" : ""
            }`}
            data-testid="drive-col-popup-item-empty"
          >
            <span
              className={`w-3.5 h-3.5 border flex items-center justify-center shrink-0 ${
                emptyOn ? "bg-amber-300 border-amber-300" : "border-neutral-500"
              }`}
            >
              {emptyOn && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
            </span>
            <span className="italic text-neutral-400">— (vide)</span>
          </button>
        )}
        {filtered.length === 0 && !hasEmpty && (
          <div className="px-3 py-3 text-xs text-neutral-500 italic">Aucune valeur</div>
        )}
        {filtered.map((v) => {
          const on = selected.has(v);
          return (
            <button
              key={v}
              onClick={() => onToggle(v)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-neutral-800 transition-colors normal-case tracking-normal ${
                on ? "bg-neutral-800" : ""
              }`}
              data-testid={`drive-col-popup-item-${v}`}
            >
              <span
                className={`w-3.5 h-3.5 border flex items-center justify-center shrink-0 ${
                  on ? "bg-amber-300 border-amber-300" : "border-neutral-500"
                }`}
              >
                {on && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
              </span>
              <span className="truncate text-white">{v}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Cell({
  field,
  value,
  sold,
  onSave,
  rowIdx,
  colIdx,
  isSelected,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  if (field.type === "select") {
    const val = value == null ? "" : String(value);
    return (
      <td
        className={`px-2 py-1.5 border-b border-neutral-200 align-top select-none ${
          isSelected
            ? "!bg-sky-200/70 outline outline-1 outline-sky-500 outline-offset-[-1px]"
            : ""
        }`}
        data-testid={`drive-cell-${field.key}`}
      >
        <select
          value={val}
          onChange={(e) => onSave(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`w-full text-xs font-mono-data border px-1 py-0.5 cursor-pointer ${
            val
              ? "bg-red-200 border-red-400 text-red-900 font-bold"
              : "bg-white border-neutral-300 text-neutral-400"
          }`}
          data-testid={`drive-select-${field.key}`}
        >
          <option value="">—</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </td>
    );
  }

  const display = (() => {
    if (value == null || value === "") return "";
    if (field.type === "money") return fmtMoney(value);
    return String(value);
  })();

  const start = () => {
    setDraft(
      value == null
        ? ""
        : field.type === "money"
        ? String(value).replace(".", ",")
        : String(value),
    );
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = () => {
    setEditing(false);
    const oldStr =
      value == null
        ? ""
        : field.type === "money"
        ? String(value).replace(".", ",")
        : String(value);
    if (draft !== oldStr) {
      onSave(draft);
    }
  };

  // Emphasis for CLIENT / RÉF VENTE columns when row is sold
  const isSaleKey = field.sale;
  const emphasisClass = sold && isSaleKey
    ? "font-bold text-emerald-900"
    : sold
    ? "text-emerald-950"
    : "text-neutral-900";

  return (
    <td
      className={`px-2 py-1.5 border-b border-neutral-200 align-top cursor-text select-none ${
        sold ? "border-b-emerald-200" : ""
      } ${
        isSelected
          ? "!bg-sky-200/70 outline outline-1 outline-sky-500 outline-offset-[-1px]"
          : ""
      }`}
      onMouseDown={(e) => !editing && onCellMouseDown?.(rowIdx, colIdx, e)}
      onMouseEnter={() => !editing && onCellMouseEnter?.(rowIdx, colIdx)}
      onMouseUp={() => !editing && onCellMouseUp?.(rowIdx, colIdx, start)}
      onDoubleClick={() => !editing && start()}
      data-testid={`drive-cell-${field.key}`}
    >
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              setEditing(false);
            }
          }}
          className="w-full bg-white border border-black px-1 py-0.5 text-xs font-mono-data focus:outline-none"
        />
      ) : (
        <div
          className={`min-h-[20px] px-1 py-0.5 whitespace-nowrap overflow-hidden text-ellipsis ${emphasisClass} ${
            display ? "" : "text-neutral-300"
          }`}
          title={display}
        >
          {display || "—"}
        </div>
      )}
    </td>
  );
}


function SyncPreviewList({ tab, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-neutral-500">
        Aucune ligne dans cette catégorie.
      </div>
    );
  }

  const config = {
    sold_now: { rowClass: "bg-emerald-50 hover:bg-emerald-100", labelColor: "text-emerald-700" },
    new_rows: { rowClass: "bg-blue-50 hover:bg-blue-100", labelColor: "text-blue-700" },
    updated: { rowClass: "bg-amber-50 hover:bg-amber-100", labelColor: "text-amber-700" },
    missing: { rowClass: "bg-red-50 hover:bg-red-100", labelColor: "text-red-700" },
    unchanged: { rowClass: "bg-neutral-50 hover:bg-neutral-100", labelColor: "text-neutral-500" },
  }[tab] || { rowClass: "", labelColor: "" };

  return (
    <table className="w-full text-xs font-mono-data">
      <thead className="sticky top-0 bg-neutral-900 text-white">
        <tr>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Date</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Catégorie</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Block</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Rang</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Siège</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Client</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Réf vente</th>
          <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Supplier</th>
          {tab === "updated" && (
            <th className="px-2 py-2 text-left uppercase tracking-widest text-[10px] font-normal">Changements</th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r._sig || r.id || i} className={`border-b border-neutral-200 ${config.rowClass}`}>
            <td className="px-2 py-1.5 lowercase">{r.date || "—"}</td>
            <td className="px-2 py-1.5">{r.categorie || "—"}</td>
            <td className="px-2 py-1.5">{r.block || "—"}</td>
            <td className="px-2 py-1.5">{r.rang || "—"}</td>
            <td className="px-2 py-1.5">{r.siege || "—"}</td>
            <td className={`px-2 py-1.5 font-bold ${config.labelColor}`}>{r.client || "—"}</td>
            <td className={`px-2 py-1.5 font-bold ${config.labelColor}`}>{r.ref_vente || "—"}</td>
            <td className="px-2 py-1.5">{r.supplier || "—"}</td>
            {tab === "updated" && (
              <td className="px-2 py-1.5 text-[10px]">
                {r._changes
                  ? Object.entries(r._changes)
                      .slice(0, 4)
                      .map(([k, ch]) => (
                        <div key={k} className="truncate">
                          <span className="text-neutral-500">{k}:</span>{" "}
                          <span className="line-through text-red-600">{String(ch.old ?? "∅")}</span>{" "}
                          → <span className="text-emerald-700 font-bold">{String(ch.new ?? "∅")}</span>
                        </div>
                      ))
                  : null}
                {r._changes && Object.keys(r._changes).length > 4 && (
                  <div className="text-neutral-500 italic">… +{Object.keys(r._changes).length - 4} autres</div>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
